import type { IconOwnProps } from '@ps/isp-web-component-library';
import type { LinkItem } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { type IspSportsAlias, landingPageEventTypeIds } from '@ps/web-sports-config';
import { generateSportsPath } from '@ps/web-sports-utils';
import { useSelector, useStore } from 'react-redux';

import { select } from '@swf/core/store';
import { EventTypeId } from '@swf/sports/types/eventType';
import { getEventTypeIcon } from '@swf/sports/utils/getEventTypeIcon';

import { isGreyhoundRacing, isHorseRacing } from '../utils/isEventTypeId';

export const defaultToMatchesTab = [
  EventTypeId.SOCCER,
  EventTypeId.TENNIS,
  EventTypeId.BASKETBALL,
  EventTypeId.AMERICAN_FOOTBALL,
  EventTypeId.BASEBALL,
  EventTypeId.ICE_HOCKEY
];

const getRoute = (
  aliases: ReturnType<typeof select.cms.getAliases>,
  { eventTypeId, type }: Pick<LinkItem, 'eventTypeId' | 'type'>,
  isEventTypeLandingPage: boolean
): string => {
  let alias: IspSportsAlias = 'sportsSport';

  switch (type) {
    case 'EVENT':
      alias = 'sportsEvent';
      break;
    case 'COMPETITION':
      alias = 'sportsCompetition';
      break;
    case 'EVENT_TYPE':
    default:
      if (isEventTypeLandingPage) {
        alias = 'sportsLanding';
        break;
      } else if (defaultToMatchesTab.includes(eventTypeId)) {
        alias = 'sportsMatchFiltering';
        break;
      }
  }

  return aliases[alias] ?? '/';
};

export interface MenuItemWithUrlAndIcon {
  icon: IconOwnProps['icon'];
  text: string;
  url: string;
}

/**
 * Gets the url based on the link type and appends the correct icon for the
 * link.
 */
const useUrlMenuItems = (menuItems?: LinkItem[]): MenuItemWithUrlAndIcon[] => {
  const isHorseRacingEnabled = useFeature(FeatureFlag.ISPEnableHorseRacing);
  const isGreyhoundsEnabled = useFeature(FeatureFlag.GreyhoundRacingEnabled);
  const aliases = useSelector(select.cms.getAliases);
  const store = useStore();

  return (
    menuItems?.reduce<MenuItemWithUrlAndIcon[]>((acc, { type, text, ...values }) => {
      if (isHorseRacing(values.eventTypeId) && !isHorseRacingEnabled) return acc;
      if (isGreyhoundRacing(values.eventTypeId) && !isGreyhoundsEnabled) return acc;

      const isEventTypeLandingPage = select.sports.isEventTypeLandingPage(store.getState(), {
        eventTypeId: values.eventTypeId,
        landingPageEventTypeIds
      });

      return [
        ...acc,
        {
          icon: getEventTypeIcon(values.eventTypeId),
          url: generateSportsPath(
            getRoute(aliases, { type, eventTypeId: values.eventTypeId }, isEventTypeLandingPage),
            {
              ...values
            }
          ),
          text
        }
      ];
    }, []) ?? []
  );
};

export default useUrlMenuItems;
